import type { MedplumClient } from '@medplum/core';
import type { Communication, Task } from '@medplum/fhirtypes';
import {
  APPOINTMENT_REQUEST_TASK_CODE,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import type { AppointmentRequestDetailsPayload } from '../../messaging/appointmentRequestMessage';
import { APPOINTMENT_REQUEST_DETAILS_EXT_URL } from '../../messaging/appointmentRequestMessage';
import { notifyClientAppointmentRequestDecision } from '../../../lib/notificationLifecycle';
import { PENDING_RESCHEDULE_TASK_STATUSES } from './rescheduleRequestTask';

export const APPOINTMENT_REQUEST_TASK = {
  system: 'http://unify.health/tasks',
  code: 'appointment-request',
  display: 'Appointment request',
} as const;

export const APPOINTMENT_REQUEST_TASK_INPUT_TYPE = 'appointment-request-details';

export async function findPendingAppointmentRequestTaskForProposed(
  medplum: MedplumClient,
  proposedAppointmentId: string,
): Promise<Task | undefined> {
  const tasks = await medplum.searchResources('Task', {
    focus: `Appointment/${proposedAppointmentId}`,
    status: PENDING_RESCHEDULE_TASK_STATUSES.join(','),
    _count: '5',
    _sort: '-_lastUpdated',
  });
  return tasks.find((task) =>
    task.code?.coding?.some(
      (c) =>
        (c.system === APPOINTMENT_REQUEST_TASK.system && c.code === APPOINTMENT_REQUEST_TASK.code) ||
        (c.system === TASK_TYPE_SYSTEM && c.code === APPOINTMENT_REQUEST_TASK_CODE),
    ),
  );
}

export async function findPendingAppointmentRequestTaskForEpisode(
  medplum: MedplumClient,
  episodeOfCareId: string,
): Promise<Task | undefined> {
  const tasks = await medplum.searchResources('Task', {
    'based-on': `EpisodeOfCare/${episodeOfCareId}`,
    status: PENDING_RESCHEDULE_TASK_STATUSES.join(','),
    _count: '5',
    _sort: '-_lastUpdated',
  });
  return tasks.find((task) =>
    task.code?.coding?.some(
      (c) =>
        (c.system === APPOINTMENT_REQUEST_TASK.system && c.code === APPOINTMENT_REQUEST_TASK.code) ||
        (c.system === TASK_TYPE_SYSTEM && c.code === APPOINTMENT_REQUEST_TASK_CODE),
    ),
  );
}

export async function finalizeAppointmentRequest(
  medplum: MedplumClient,
  params: {
    communicationId?: string;
    decision: 'accept' | 'deny';
    proposedAppointmentId?: string;
  },
): Promise<void> {
  let requestMessage: Communication | undefined;
  let updatedDetails: AppointmentRequestDetailsPayload | undefined;

  if (params.communicationId) {
    requestMessage = await medplum.readResource('Communication', params.communicationId);
    const extensions = [...(requestMessage.extension ?? [])];
    const detailsIndex = extensions.findIndex((e) => e.url === APPOINTMENT_REQUEST_DETAILS_EXT_URL);
    if (detailsIndex >= 0) {
      const raw = extensions[detailsIndex]?.valueString;
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as AppointmentRequestDetailsPayload;
          parsed.providerDecision = params.decision;
          parsed.respondedAt = new Date().toISOString();
          updatedDetails = parsed;
          extensions[detailsIndex] = {
            ...extensions[detailsIndex]!,
            valueString: JSON.stringify(parsed),
          };
        } catch {
          // leave extension unchanged
        }
      }
    }

    await medplum.updateResource({
      ...requestMessage,
      extension: extensions,
    });
  }

  // No Task write: the bot's Appointment subscription completes/cancels the Task when the Appointment is booked/cancelled.
  const proposedAppointmentId =
    params.proposedAppointmentId ?? updatedDetails?.proposedAppointmentId?.trim();

  const patientId = updatedDetails?.patientId?.trim();
  if (!patientId) return;

  const threadRef = requestMessage?.partOf?.[0]?.reference;
  const threadId = threadRef?.startsWith('Communication/')
    ? threadRef.slice('Communication/'.length)
    : undefined;

  try {
    await notifyClientAppointmentRequestDecision(medplum, {
      patientId,
      decision: params.decision,
      preferredWhen: updatedDetails?.preferredWhen,
      threadId,
      requestCommunicationId: params.communicationId,
      proposedAppointmentId,
    });
  } catch (err) {
    console.warn('[finalizeAppointmentRequest] Could not create bell notification:', err);
  }
}
